import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import {
    isFeatureFace,
    isFeatureLine,
    isFeaturePoint,
} from '../../mesh/feature';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import { resolveBRepGeometryDescriptor, resolveTopologySelectionPick } from '../../utils/feature';
import type { MeasureEntityRef } from '../types/measure-types';
import { MeasureUnboundedGeometryAdapter } from '../unbounded/MeasureUnboundedGeometryAdapter';
import {
    MeasureDistancePrimitiveKind,
    type MeasureDistanceGeometry,
} from '../types/measure-distance-types';
import { MeasureGeometryKind, type MeasureMetricQueryContext } from '../types/measure-property-types';
import { classifyMeasureGeometry } from './MeasurePropertyResolver';
import { buildMeasureMetricQueryContext } from './MeasureMetricQueryContextBuilder';
import { MeasureMetricCalculator } from './MeasureMetricCalculator';

/** 折线最少点数。 */
const MIN_POLYLINE_POINT_COUNT = 2;

/**
 * 测量实体几何适配器。
 *
 * 职责：将测量选中引用归一化为 {@link MeasureDistanceGeometry}，供最近点求解器消费。
 */
export class MeasureEntityGeometryAdapter {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 度量计算器（复用边采样与几何分类）。 */
    private readonly metricCalculator: MeasureMetricCalculator;

    /** 无边界测量几何适配器。 */
    private readonly unboundedGeometryAdapter: MeasureUnboundedGeometryAdapter;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.metricCalculator = new MeasureMetricCalculator();
        this.unboundedGeometryAdapter = new MeasureUnboundedGeometryAdapter(viewer);
    }

    /**
     * 将测量实体引用适配为求距几何基元。
     *
     * @param entityRef 测量实体引用。
     * @returns 求距几何体；无法适配时返回 null。
     */
    public adapt(entityRef: MeasureEntityRef): MeasureDistanceGeometry | null {
        if (entityRef.unboundedBinding) {
            return this.unboundedGeometryAdapter.adapt(entityRef.unboundedBinding);
        }

        const sceneObject = this.viewer.findSceneObjectById(entityRef.objectId);
        if (!sceneObject) {
            return null;
        }

        const topologyPick = resolveTopologySelectionPick(sceneObject);
        const brepDescriptor = resolveBRepGeometryDescriptor(sceneObject);
        const context = buildMeasureMetricQueryContext(sceneObject, topologyPick, brepDescriptor);

        if (isDatumPoint(sceneObject) || isFeaturePoint(sceneObject)) {
            return this.adaptPointEntity(sceneObject, context, brepDescriptor);
        }

        if (isDatumAxis(sceneObject)) {
            return this.adaptDatumAxis(sceneObject);
        }

        if (isDatumPlaneObject(sceneObject)) {
            return this.adaptDatumPlane(sceneObject);
        }

        const geometryKind = classifyMeasureGeometry(context, this.metricCalculator);

        switch (geometryKind) {
            case MeasureGeometryKind.Vertex:
                return this.adaptPointEntity(sceneObject, context, brepDescriptor);
            case MeasureGeometryKind.StraightEdge:
                return this.adaptStraightEdge(context, brepDescriptor);
            case MeasureGeometryKind.ArcEdge:
            case MeasureGeometryKind.FullCircleEdge:
                return this.adaptPolylineEdge(context);
            case MeasureGeometryKind.PlaneFace:
                return this.adaptPlaneFace(sceneObject, brepDescriptor);
            case MeasureGeometryKind.CircularSurfaceFace:
            case MeasureGeometryKind.BoundedSurfaceFace:
                return this.adaptMeshFace(sceneObject);
            default:
                return this.adaptFallback(sceneObject, context, brepDescriptor);
        }
    }

    /**
     * 适配点实体。
     *
     * @param sceneObject 场景对象。
     * @param context 度量上下文。
     * @param brepDescriptor BRep 描述符。
     * @returns 点基元；失败时返回 null。
     */
    private adaptPointEntity(
        sceneObject: THREE.Object3D,
        context: MeasureMetricQueryContext,
        brepDescriptor: BRepGeometryDescriptor | null,
    ): MeasureDistanceGeometry | null {
        if (brepDescriptor?.entityKind === 'vertex') {
            const tuple = brepDescriptor.point;
            return {
                kind: MeasureDistancePrimitiveKind.Point,
                position: new THREE.Vector3(tuple[0], tuple[1], tuple[2]),
            };
        }

        if (isFeaturePoint(sceneObject)) {
            return {
                kind: MeasureDistancePrimitiveKind.Point,
                position: sceneObject.getWorldPoint().clone(),
            };
        }

        if (isDatumPoint(sceneObject)) {
            return {
                kind: MeasureDistancePrimitiveKind.Point,
                position: sceneObject.getWorldPoint().clone(),
            };
        }

        const metrics = this.metricCalculator.computeVertexMetrics(context);
        if (!metrics) {
            return null;
        }
        const tuple = metrics.worldPoint;
        return {
            kind: MeasureDistancePrimitiveKind.Point,
            position: new THREE.Vector3(tuple[0], tuple[1], tuple[2]),
        };
    }

    /**
     * 适配直线边。
     *
     * @param context 度量上下文。
     * @param brepDescriptor BRep 描述符。
     * @returns 线段基元；失败时返回 null。
     */
    private adaptStraightEdge(
        context: MeasureMetricQueryContext,
        brepDescriptor: BRepGeometryDescriptor | null,
    ): MeasureDistanceGeometry | null {
        if (brepDescriptor?.entityKind === 'edge' && brepDescriptor.curveKind === 'line') {
            const start = brepDescriptor.start;
            const end = brepDescriptor.end;
            return {
                kind: MeasureDistancePrimitiveKind.Segment,
                start: new THREE.Vector3(start[0], start[1], start[2]),
                end: new THREE.Vector3(end[0], end[1], end[2]),
            };
        }

        const lineObject = context.sceneObject;
        if (isFeatureLine(lineObject)) {
            const endpoints = lineObject.getWorldEndpoints();
            if (endpoints) {
                return {
                    kind: MeasureDistancePrimitiveKind.Segment,
                    start: endpoints.start.clone(),
                    end: endpoints.end.clone(),
                };
            }
        }

        return this.adaptPolylineEdge(context);
    }

    /**
     * 适配折线边（圆弧/样条等）。
     *
     * @param context 度量上下文。
     * @returns 折线基元；失败时返回 null。
     */
    private adaptPolylineEdge(context: MeasureMetricQueryContext): MeasureDistanceGeometry | null {
        const points = this.metricCalculator.resolveEdgeWorldPolylinePoints(context);
        if (points.length < MIN_POLYLINE_POINT_COUNT) {
            return null;
        }
        return {
            kind: MeasureDistancePrimitiveKind.Polyline,
            points,
        };
    }

    /**
     * 适配有边界平面特征面。
     *
     * SolidWorks 语义：模型上选中的拓扑面为有限面域，求距时必须在面边界内取最近点。
     * 若无法获取面代理，则退化为 BRep 无限平面（缺少边界信息时的兜底）。
     *
     * @param sceneObject 场景对象。
     * @param brepDescriptor BRep 描述符。
     * @returns 求距几何体；失败时返回 null。
     */
    private adaptPlaneFace(
        sceneObject: THREE.Object3D,
        brepDescriptor: BRepGeometryDescriptor | null,
    ): MeasureDistanceGeometry | null {
        const meshFace = this.adaptMeshFace(sceneObject);
        if (meshFace) {
            return meshFace;
        }

        if (brepDescriptor?.entityKind === 'face' && brepDescriptor.surfaceKind === 'plane') {
            const point = brepDescriptor.point;
            const normal = brepDescriptor.normal;
            return {
                kind: MeasureDistancePrimitiveKind.Plane,
                point: new THREE.Vector3(point[0], point[1], point[2]),
                normal: new THREE.Vector3(normal[0], normal[1], normal[2]).normalize(),
            };
        }

        return null;
    }

    /**
     * 适配网格面（有边界拓扑面，含平面与解析曲面）。
     *
     * @param sceneObject 场景对象。
     * @returns 网格面基元；失败时返回 null。
     */
    private adaptMeshFace(sceneObject: THREE.Object3D): MeasureDistanceGeometry | null {
        if (!isFeatureFace(sceneObject)) {
            return null;
        }
        return {
            kind: MeasureDistancePrimitiveKind.MeshFace,
            face: sceneObject,
        };
    }

    /**
     * 未分类几何的兜底适配。
     *
     * @param sceneObject 场景对象。
     * @param context 度量上下文。
     * @param brepDescriptor BRep 描述符。
     * @returns 求距几何体；失败时返回 null。
     */
    private adaptFallback(
        sceneObject: THREE.Object3D,
        context: MeasureMetricQueryContext,
        brepDescriptor: BRepGeometryDescriptor | null,
    ): MeasureDistanceGeometry | null {
        if (isFeatureFace(sceneObject)) {
            return this.adaptMeshFace(sceneObject);
        }

        const planeFace = this.adaptPlaneFace(sceneObject, brepDescriptor);
        if (planeFace) {
            return planeFace;
        }

        if (isFeatureLine(sceneObject)) {
            return this.adaptPolylineEdge(context);
        }

        return null;
    }

    /**
     * 适配基准轴。
     *
     * @param axisObject 基准轴对象。
     * @returns 线段或无限直线基元；失败时返回 null。
     */
    private adaptDatumAxis(axisObject: THREE.Object3D): MeasureDistanceGeometry | null {
        if (!isDatumAxis(axisObject)) {
            return null;
        }

        const endpoints = axisObject.getWorldEndpoints();
        if (endpoints) {
            return {
                kind: MeasureDistancePrimitiveKind.Segment,
                start: endpoints.start.clone(),
                end: endpoints.end.clone(),
            };
        }

        return {
            kind: MeasureDistancePrimitiveKind.InfiniteLine,
            origin: axisObject.originWorld.clone(),
            direction: axisObject.getWorldDirection(),
        };
    }

    /**
     * 适配基准面（无限延伸平面）。
     *
     * SolidWorks 语义：基准面/参考面在求距时按无限平面处理。
     *
     * @param planeObject 基准面对象。
     * @returns 无限平面基元；失败时返回 null。
     */
    private adaptDatumPlane(planeObject: THREE.Object3D): MeasureDistanceGeometry | null {
        if (!isDatumPlaneObject(planeObject)) {
            return null;
        }

        let referencePlane: THREE.Plane | null = null;
        planeObject.traverse((child) => {
            if (referencePlane) {
                return;
            }
            const candidate = Reflect.get(child, 'referencePlane');
            if (candidate instanceof THREE.Plane) {
                referencePlane = candidate;
            }
        });

        if (!referencePlane) {
            return null;
        }

        const normal = referencePlane.normal.clone().normalize();
        const point = referencePlane.coplanarPoint(new THREE.Vector3());
        planeObject.localToWorld(point);
        normal.transformDirection(planeObject.matrixWorld).normalize();

        return {
            kind: MeasureDistancePrimitiveKind.Plane,
            point,
            normal,
        };
    }
}
